import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Globe, Building, Train, MapPin, Search, ChevronLeft, ChevronRight, Upload, Image as ImageIcon } from 'lucide-react';
import { buildApiUrl, API_CONFIG } from '../config/api';

interface Region {
  id: number;
  name: string;
  country: string;
  url_code?: string;
  timezone: string;
  currency: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
  train_companies_count: number;
  total_lines_count: number;
  total_stations_count: number;
}

const RegionManager: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [filteredRegions, setFilteredRegions] = useState<Region[]>([]);
  const [paginatedRegions, setPaginatedRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newRegion, setNewRegion] = useState<Partial<Region>>({
    name: '',
    country: '',
    timezone: 'UTC',
    currency: 'USD',
  });

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredRegions(regions);
    } else {
      const filtered = regions.filter(region =>
        region.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        region.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        region.currency.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRegions(filtered);
    }
  }, [regions, searchTerm]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedRegions(filteredRegions.slice(startIndex, endIndex));
  }, [filteredRegions, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchRegions = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REGIONS));
      const data = await response.json();
      setRegions(data.regions || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REGIONS), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRegion),
      });

      if (response.ok) {
        await fetchRegions();
        setIsCreating(false);
        setNewRegion({ name: '', country: '', timezone: 'UTC', currency: 'USD' });
      }
    } catch (error) {
      console.error('Error creating region:', error);
    }
  };

  const handleUpdate = async (region: Region) => {
    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.REGIONS}/${region.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(region),
      });

      if (response.ok) {
        await fetchRegions();
        setEditingRegion(null);
      }
    } catch (error) {
      console.error('Error updating region:', error);
    }
  };

  const handleDelete = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this region? This will affect all related train companies, lines, and stations.')) return;

    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.REGIONS}/${id}`), {
        method: 'DELETE',
      });

      if (response.ok || response.status === 204) {
        await fetchRegions();
      } else if (response.status === 400) {
        const errorData = await response.json();
        alert(`Error: ${errorData.detail}`);
      } else {
        console.error('Delete failed with status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        alert('Failed to delete region. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting region:', error);
      alert('Failed to delete region. Please check your connection.');
    }
  };

  const handleImageUpload = async (regionId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.REGIONS}/${regionId}/upload-image`), {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        await fetchRegions();
        alert('Image uploaded successfully!');
      } else {
        const errorData = await response.json();
        alert(`Error uploading image: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <Globe className="h-6 w-6 mr-2 text-admin-600" />
          Region Management
        </h2>
        <button
          onClick={() => setIsCreating(true)}
          className="admin-btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Region
        </button>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="admin-card mb-6">
          <h3 className="text-lg font-semibold mb-4">Create New Region</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <input
              type="text"
              placeholder="Region Name (e.g., Bangkok)"
              value={newRegion.name}
              onChange={(e) => setNewRegion({ ...newRegion, name: e.target.value })}
              className="admin-input"
            />
            <input
              type="text"
              placeholder="Country (e.g., Thailand)"
              value={newRegion.country}
              onChange={(e) => setNewRegion({ ...newRegion, country: e.target.value })}
              className="admin-input"
            />
            <input
              type="text"
              placeholder="URL Code (e.g., th)"
              value={newRegion.url_code || ''}
              onChange={(e) => setNewRegion({ ...newRegion, url_code: e.target.value })}
              className="admin-input"
              maxLength={10}
            />
            <input
              type="text"
              placeholder="Timezone (e.g., Asia/Bangkok)"
              value={newRegion.timezone}
              onChange={(e) => setNewRegion({ ...newRegion, timezone: e.target.value })}
              className="admin-input"
            />
            <input
              type="text"
              placeholder="Currency (e.g., THB)"
              value={newRegion.currency}
              onChange={(e) => setNewRegion({ ...newRegion, currency: e.target.value })}
              className="admin-input"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreate} className="btn-primary flex items-center">
              <Save className="h-4 w-4 mr-2" />
              Save
            </button>
            <button
              onClick={() => setIsCreating(false)}
              className="btn-secondary flex items-center"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search regions by name, country, or currency..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-input pl-10 w-full max-w-md"
          />
        </div>
      </div>

      {/* Regions Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {paginatedRegions.map((region) => (
          <div key={region.id} className="admin-card">
            {editingRegion?.id === region.id ? (
              <div>
                <h3 className="text-lg font-semibold mb-4">Edit Region</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editingRegion.name}
                    onChange={(e) => setEditingRegion({ ...editingRegion, name: e.target.value })}
                    className="admin-input text-sm"
                    placeholder="Region Name"
                  />
                  <input
                    type="text"
                    value={editingRegion.country}
                    onChange={(e) => setEditingRegion({ ...editingRegion, country: e.target.value })}
                    className="admin-input text-sm"
                    placeholder="Country"
                  />
                  <input
                    type="text"
                    value={editingRegion.url_code || ''}
                    onChange={(e) => setEditingRegion({ ...editingRegion, url_code: e.target.value })}
                    className="admin-input text-sm"
                    placeholder="URL Code (e.g., th)"
                    maxLength={10}
                  />
                  <input
                    type="text"
                    value={editingRegion.timezone}
                    onChange={(e) => setEditingRegion({ ...editingRegion, timezone: e.target.value })}
                    className="admin-input text-sm"
                    placeholder="Timezone"
                  />
                  <input
                    type="text"
                    value={editingRegion.currency}
                    onChange={(e) => setEditingRegion({ ...editingRegion, currency: e.target.value })}
                    className="admin-input text-sm"
                    placeholder="Currency"
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleUpdate(editingRegion)}
                    className="btn-primary flex items-center text-sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={() => setEditingRegion(null)}
                    className="btn-secondary flex items-center text-sm"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {/* Region Image */}
                {region.image_url && (
                  <div className="mb-4 rounded-lg overflow-hidden">
                    <img
                      src={`${API_CONFIG.BASE_URL}${region.image_url}`}
                      alt={region.name}
                      className="w-full h-40 object-cover"
                    />
                  </div>
                )}

                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Globe className="h-5 w-5 mr-2 text-admin-600" />
                      {region.name}
                    </h3>
                    <p className="text-sm text-gray-600">{region.country}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingRegion(region)}
                      className="text-admin-600 hover:text-admin-900 p-1"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(region.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {region.url_code && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">URL Code:</span>
                      <span className="font-medium font-mono bg-gray-100 px-2 py-1 rounded">/{region.url_code}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Timezone:</span>
                    <span className="font-medium">{region.timezone}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Currency:</span>
                    <span className="font-medium">{region.currency}</span>
                  </div>
                </div>

                {/* Image Upload Button */}
                <div className="mb-4">
                  <label
                    htmlFor={`image-upload-${region.id}`}
                    className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-sm"
                  >
                    {region.image_url ? (
                      <>
                        <ImageIcon className="h-4 w-4" />
                        Change Image
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Upload Image
                      </>
                    )}
                  </label>
                  <input
                    id={`image-upload-${region.id}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleImageUpload(region.id, file);
                      }
                    }}
                  />
                </div>

                {/* Statistics */}
                <div className="border-t pt-4 space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Statistics</h4>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="flex flex-col items-center p-2 bg-blue-50 rounded">
                      <Building className="h-4 w-4 text-blue-600 mb-1" />
                      <span className="text-xs text-gray-600">Companies</span>
                      <span className="font-semibold text-blue-600">{region.train_companies_count}</span>
                    </div>
                    <div className="flex flex-col items-center p-2 bg-green-50 rounded">
                      <Train className="h-4 w-4 text-green-600 mb-1" />
                      <span className="text-xs text-gray-600">Lines</span>
                      <span className="font-semibold text-green-600">{region.total_lines_count}</span>
                    </div>
                    <div className="flex flex-col items-center p-2 bg-purple-50 rounded">
                      <MapPin className="h-4 w-4 text-purple-600 mb-1" />
                      <span className="text-xs text-gray-600">Stations</span>
                      <span className="font-semibold text-purple-600">{region.total_stations_count}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {filteredRegions.length > 0 && (
        <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200 mt-6 rounded-lg">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredRegions.length)} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredRegions.length)} of {filteredRegions.length} results
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.ceil(filteredRegions.length / itemsPerPage) }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === Math.ceil(filteredRegions.length / itemsPerPage) ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                )
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-admin-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(Math.ceil(filteredRegions.length / itemsPerPage), currentPage + 1))}
              disabled={currentPage === Math.ceil(filteredRegions.length / itemsPerPage)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionManager;